import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

interface Organization {
  id: string;
  name: string;
  shortName?: string;
  region?: string;
}

interface OrganizationApiResponse {
  id: string;
  name: string;
  shortName?: string;
  region?: string;
}

const fetchOrganizationsFromService = async (
  bearerToken: string,
  customerId: string
): Promise<Organization[]> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/gcorgs/${customerId}`;

    const response = await authenticatedFetch(url, {
      headers: getV2AuthHeaders(bearerToken)
    });

    if (!response.ok) {
      logger.error('Fetch organizations API returned error', {
        status: response.status,
        statusText: response.statusText,
        customerId,
        url
      });
      throw new Error(`Error fetching organizations: ${response.status} ${response.statusText}`);
    }

    const data: OrganizationApiResponse[] = await response.json();
    logger.debug('Organizations API response received', {
      customerId,
      count: data.length
    });

    // Map the API response to your Organization interface
    const mappedOrgs: Organization[] = data.map((organization: OrganizationApiResponse) => {
      const org: Organization = {
        id: organization.id,
        name: organization.name
      };
      if (organization.shortName) {
        org.shortName = organization.shortName;
      }
      if (organization.region) {
        org.region = organization.region;
      }
      return org;
    });

    logger.info('Organizations fetched successfully', {
      customerId,
      count: mappedOrgs.length,
      organizations: mappedOrgs.map(o => ({ id: o.id, name: o.name, region: o.region }))
    });

    return mappedOrgs;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to fetch organizations', {
      error: msg,
      customerId,
      url: `${getEnv().VITE_EDCI_API_URL}/api/gcorgs/${customerId}`
    });
    throw error;
  }
};

export default fetchOrganizationsFromService;
